import { FormBuilderClient } from '@/components/FormBuilder/FormBuilderClient'
import type { BuilderSnapshot } from '@/stores/formBuilderStore'
import { getFormTemplateDetail, saveFormTemplateSnapshot } from '@/lib/data-client/form-templates'
import { useAuth } from '@/lib/auth'
import { hasFormAdminAccess } from '@/lib/permissions'
import { useSystemFeatureVisibility } from '@/lib/system-feature-visibility'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useNavigate, useParams } from '@tanstack/react-router'
import { useEffect } from 'react'
import { toast } from 'sonner'

export default function FormTemplateBuilderRoute() {
  const params = useParams({ strict: false }) as { templateId?: string }
  const templateId = params.templateId ?? ''
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { user, isLoading } = useAuth()
  const canManageForms = hasFormAdminAccess(user)
  const featureVisibility = useSystemFeatureVisibility()
  const templatesEnabled = featureVisibility['forms.templates']
  const { data: template, isLoading: isTemplateLoading } = useQuery({
    queryKey: ['form-template-detail', templateId],
    queryFn: () => getFormTemplateDetail(templateId),
    enabled: Boolean(templateId) && canManageForms && templatesEnabled,
  })

  useEffect(() => {
    if (!isLoading && (!canManageForms || !templatesEnabled)) {
      void navigate({ to: '/dashboard/form-templates' })
    }
  }, [canManageForms, isLoading, navigate, templatesEnabled])

  async function saveTemplateSnapshot(snapshot: BuilderSnapshot) {
    if (!template) return
    await saveFormTemplateSnapshot(template.id, {
      form: snapshot.form,
      questions: snapshot.questions,
      logicJumps: snapshot.logicJumps,
      groups: snapshot.questionGroups,
      outline: snapshot.questionOutline,
      hiddenFields: template.snapshot.hiddenFields,
    })
    await queryClient.invalidateQueries({ queryKey: ['form-templates'] })
    await queryClient.invalidateQueries({ queryKey: ['form-template-detail', template.id] })
  }

  if (isLoading || isTemplateLoading) {
    return <div className="grid min-h-dvh place-items-center text-sm text-slate-500">載入中...</div>
  }

  if (!template) {
    return (
      <div className="grid min-h-dvh place-items-center bg-slate-50 p-6 text-center">
        <div>
          <h1 className="text-lg font-semibold text-slate-950">找不到問卷範本</h1>
          <p className="mt-2 text-sm text-slate-500">請返回問卷範本清單重新選擇。</p>
        </div>
      </div>
    )
  }

  return (
    <FormBuilderClient
      initialForm={{
        ...template.snapshot.form,
        questions: template.snapshot.questions,
        logicJumps: template.snapshot.logicJumps,
        hiddenFields: template.snapshot.hiddenFields,
        groups: template.snapshot.groups,
        outline: template.snapshot.outline,
        responseCount: 0,
        completionRate: 0,
        sourceMeta: template.sourceMeta,
      }}
      variant="template"
      backTo="/dashboard/form-templates"
      onSaveBuilderSnapshot={async (snapshot) => {
        await saveTemplateSnapshot(snapshot)
        toast.success('範本已儲存')
      }}
    />
  )
}
